// eslint-disable-next-line no-restricted-imports
import React, { useEffect } from "react";

import { RemixBrowser, useLocation, useMatches } from "@remix-run/react";
import { hydrateRoot } from "react-dom/client";

import { setupSentry } from "~/lib/sentry/client";

setupSentry();

function hydrate() {
    // AIDEV-NOTE: Only run verbose hydration diagnostics when not in production
    if (__HYDRATION_DEBUG__) {
        console.group("🔍 HYDRATION DEBUG - Environment");
        console.log("Window available:", typeof window !== "undefined");
        console.log("Document available:", typeof document !== "undefined");
        console.log("User Agent:", navigator.userAgent);
        console.log("Screen dimensions:", {
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
            devicePixelRatio: window.devicePixelRatio,
        });
        console.log("Development mode:", import.meta.env.DEV);
        console.log("Environment mode:", import.meta.env.MODE);
        console.log("Base URL:", import.meta.env.BASE_URL);
        console.groupEnd();
    }

    // Log client-side HTML before hydration (debug only)
    let preHydrationHTML: string | null = null;
    if (__HYDRATION_DEBUG__) {
        console.group("💻 CLIENT HTML BEFORE HYDRATION");
        preHydrationHTML = document.documentElement.outerHTML;
        console.log("Full HTML length:", preHydrationHTML.length);
        console.log("HEAD section (first 1000 chars):");
        console.log(document.head.innerHTML.substring(0, 1000));
        console.log("BODY start (first 1000 chars):");
        console.log(document.body.innerHTML.substring(0, 1000));

        // Check for suspicious elements
        console.log(
            "Portal elements:",
            document.querySelectorAll("[id*='portal']").length,
        );
        console.log("Style tags:", document.querySelectorAll("style").length);
        console.log("Script tags:", document.querySelectorAll("script").length);
        console.log(
            "React root attributes:",
            document.getElementById("root")?.attributes,
        );
        console.groupEnd();
    }

    // Set up mutation observer to catch DOM changes during hydration (debug only)
    const mutations: MutationRecord[] = [];
    let observer: MutationObserver | null = null;
    if (__HYDRATION_DEBUG__) {
        observer = new MutationObserver((mutationList) => {
            mutations.push(...mutationList);
        });
        observer.observe(document.documentElement, {
            childList: true,
            attributes: true,
            characterData: true,
            subtree: true,
            attributeOldValue: true,
            characterDataOldValue: true,
        });
    }

    // Track hydration errors in detail (stored in-memory; only logged in debug)
    const hydrationErrors: Array<{
        error: unknown;
        errorInfo: unknown;
        timestamp: number;
    }> = [];

    React.startTransition(() => {
        hydrateRoot(
            document,
            <React.StrictMode>
                <RemixBrowser />
            </React.StrictMode>,
            {
                onRecoverableError: (error, errorInfo) => {
                    // Ignore recoverable hydration errors that originate from the <head> section.
                    // We identify those by checking common head-level elements in the component stack.
                    const isHeadMismatch =
                        /\b(head|title|meta|link|style)\b/i.test(
                            errorInfo.componentStack ?? "",
                        );
                    if (isHeadMismatch) {
                        return; // Silently ignore
                    }

                    const errorData = {
                        error,
                        errorInfo,
                        timestamp: Date.now(),
                    };
                    hydrationErrors.push(errorData);
                    // AIDEV-NOTE: Do not log hydration error details in production
                    if (__HYDRATION_DEBUG__) {
                        console.group("⚠️ HYDRATION ERROR DETAILS");
                        console.error(
                            "Component stack",
                            errorInfo.componentStack,
                        );
                        // Log DOM state at time of error
                        console.log(
                            "DOM mutations recorded:",
                            mutations.length,
                        );
                        if (mutations.length > 0) {
                            console.log(
                                "Last 5 mutations:",
                                mutations.slice(-5).map((m) => ({
                                    type: m.type,
                                    target: m.target.nodeName,
                                    attributeName: m.attributeName,
                                    addedNodes: m.addedNodes.length,
                                    removedNodes: m.removedNodes.length,
                                })),
                            );
                        }
                        console.groupEnd();
                    }
                },
            },
        );
    });

    // Log results after hydration (debug only)
    setTimeout(() => {
        if (observer) observer.disconnect();

        if (__HYDRATION_DEBUG__) {
            console.group("💻 CLIENT HTML AFTER HYDRATION");
            const postHydrationHTML = document.documentElement.outerHTML;
            console.log("Full HTML length:", postHydrationHTML.length);
            if (preHydrationHTML != null) {
                console.log(
                    "Length difference:",
                    postHydrationHTML.length - preHydrationHTML.length,
                );
            }

            // Log hydration summary
            console.group("📊 HYDRATION SUMMARY");
            console.log("Total hydration errors:", hydrationErrors.length);
            console.log("Total DOM mutations:", mutations.length);

            if (mutations.length > 0) {
                const mutationSummary = mutations.reduce(
                    (acc, m) => {
                        acc[m.type] = (acc[m.type] || 0) + 1;
                        return acc;
                    },
                    {} as Record<string, number>,
                );
                console.log("Mutation types:", mutationSummary);

                // Find first significant mutation
                const significantMutation = mutations.find(
                    (m) =>
                        m.type === "childList" &&
                        (m.addedNodes.length > 0 || m.removedNodes.length > 0),
                );
                if (significantMutation) {
                    console.log("First significant mutation:", {
                        type: significantMutation.type,
                        target: significantMutation.target,
                        addedNodes: Array.from(
                            significantMutation.addedNodes,
                        ).map((n) => n.nodeName),
                        removedNodes: Array.from(
                            significantMutation.removedNodes,
                        ).map((n) => n.nodeName),
                    });
                }
            }

            // Check specific elements that commonly cause hydration issues
            console.group("🎯 Common Hydration Issue Elements");
            console.log(
                "ClientOnly components:",
                document.querySelectorAll("[data-client-only]").length,
            );
            console.log(
                "Suspense boundaries:",
                document.querySelectorAll("[data-suspense]").length,
            );
            console.log(
                "Dynamic styles:",
                document.querySelectorAll(
                    "style[data-emotion], style[data-styled]",
                ).length,
            );
            console.log(
                "React portals:",
                document.querySelectorAll("[data-react-portal]").length,
            );
            console.groupEnd();

            console.groupEnd(); // HYDRATION SUMMARY
            console.groupEnd(); // CLIENT HTML AFTER HYDRATION
        }
    }, 500);
}

hydrate();
